import { ReactNode, useEffect, useMemo, useState } from "react";
import { Localized, useLocalization } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  LoadingShim,
  ResponsiveFlex,
  Row,
  TruncatedAccount,
} from "@narmi/design_system";
import { catcat as cc, ProgressButtons } from "cerulean";
import { modules, useFeature } from "byzantine";
import type { ReactLocalization } from "@fluent/react";
import { DateTime } from "luxon";
import { useFingerPrintData } from "../../fingerprint";
import HeaderWithSteps from "../../HeaderWithSteps";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { TOTAL_STEPS } from "./AccountSelectScreen";
import {
  TRANSFER_DETAILS_ROUTE,
  TRANSFER_RECEIPT_ROUTE,
  TRANSFERS_ROUTE,
} from "./TransferRoutes";
import styles from "./TransferReview.module.scss";

export const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function truncateInstitutionName(value: string) {
  const words = value.trim().split(/\s+/);

  if (words.length <= 2) {
    return value;
  }

  return `${words[0]} ... ${words[words.length - 1]}`;
}

function formatFinancialInstitutionValue(fiName?: string, routing?: string) {
  if (!fiName && !routing) {
    return undefined;
  }

  if (!fiName) {
    return routing;
  }

  const truncatedName = truncateInstitutionName(fiName);

  return (
    <span title={fiName}>
      {truncatedName}
      {routing ? ` - ${routing}` : ""}
    </span>
  );
}

function getFromAccountValue({
  shouldShowPullAuthorizationDetails,
  sourceAccount,
  l10n,
}: {
  shouldShowPullAuthorizationDetails: boolean;
  sourceAccount: ReturnType<typeof modules.transfers.useSelectedSourceAccount>;
  l10n: ReactLocalization;
}) {
  if (!shouldShowPullAuthorizationDetails) {
    return undefined;
  }

  if (sourceAccount?.name && sourceAccount?.ach_pull_authorization_number) {
    return l10n.getString(
      "transfer-review-account-description-unmasked",
      {
        accountName: sourceAccount.name,
        accountNumber: sourceAccount.ach_pull_authorization_number,
      },
      `${sourceAccount.name} - ${sourceAccount.ach_pull_authorization_number}`
    );
  }

  return [sourceAccount?.name, sourceAccount?.ach_pull_authorization_number]
    .filter(Boolean)
    .join(" - ");
}

function DetailRow({
  name,
  value,
}: {
  name: string;
  value?: string | ReactNode;
}) {
  return (
    <Row>
      <Row.Item shrink>
        <p className="fontColor--secondary padding--top--s">{name}</p>
      </Row.Item>
      <Row.Item>
        <div
          className={cc(
            "padding--top--s alignChild--right--center",
            styles.rightAlign
          )}
        >
          <div className={styles.maxWidth}>{value || "-"}</div>
        </div>
      </Row.Item>
    </Row>
  );
}

export default function TransferReview() {
  const form = modules.transfers.TransferForm.useForm();
  const sourceAccount = modules.transfers.useSelectedSourceAccount();
  const destinationAccount = modules.transfers.useSelectedDestinationAccount();
  const isExternalTransfer = modules.transfers.useIsExternalTransfer();
  const receiptReady = modules.transfers.useReceiptReady();
  const fee = modules.transfers.useCalculateInstantTransferFee();
  const paymentId = modules.transfers.useSubmittedTransferId();
  const { featureEnabled, featureEquals } = useFeature();
  const idempotencyKey = useMemo(() => uuidv4() as UUID, []);
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const institution = useInstitutionSettings();
  const { submitTransfer, loading } =
    modules.transfers.useSubmitTransfer(idempotencyKey);
  const { getInstantPayment } =
    modules.transfers.useGetDownstreamInstantPayment();
  const { deviceId, deviceRequestId } = useFingerPrintData("transfers");

  const [polling, setPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const ONE_SECOND = 1000;
  const MAX_POLL_COUNT = 5;
  const institutionName = institution.short_name || institution.long_name || "";
  const supportPhoneNumber = institution.help_phone || "";
  const shouldShowPullAuthorizationDetails = Boolean(
    featureEnabled("ach_pull_authorization_language") &&
      sourceAccount?.isExternal() &&
      !form.values.instant
  );
  const fromAccountValue = getFromAccountValue({
    shouldShowPullAuthorizationDetails,
    sourceAccount,
    l10n,
  });

  const fromFinancialInstitutionValue = formatFinancialInstitutionValue(
    sourceAccount?.fi_name,
    sourceAccount?.routing
  );

  useEffect(() => {
    if (receiptReady || pollCount > MAX_POLL_COUNT) {
      setPolling(false);
      navigate(`/${TRANSFERS_ROUTE}/${TRANSFER_RECEIPT_ROUTE}`);
    }
  }, [receiptReady, pollCount]);

  useEffect(() => {
    if (polling && paymentId) {
      const intervalId = setInterval(() => {
        getInstantPayment();
        setPollCount(pollCount + 1);
        if (pollCount > MAX_POLL_COUNT) {
          clearInterval(intervalId);
        }
      }, ONE_SECOND);
      // clear on un-mount
      return () => clearInterval(intervalId);
    }
    return () => {};
  }, [polling, paymentId, pollCount]);

  useEffect(() => {
    form.submitForm().then(({ success }) => {
      if (!success) {
        navigate(`/${TRANSFERS_ROUTE}`);
      }
    });
  }, []);

  useEffect(() => {
    if (
      isExternalTransfer &&
      featureEquals("third_party_ach_transaction_evaluations", [
        "report",
        "decision",
      ])
    ) {
      form.setFieldValue("deviceId", deviceId, true);
      form.setFieldValue("deviceRequestId", deviceRequestId, true);
    }
  }, [isExternalTransfer, featureEquals, deviceId, deviceRequestId]);

  async function handleSubmit() {
    setPolling(true);
    const { error } = await submitTransfer();
    if (error) {
      setPolling(false);
    }
  }

  return (
    <LoadingShim isLoading={loading || polling}>
      <ResponsiveFlex gapSize="m">
        <HeaderWithSteps
          headerText={l10n.getString("header-review-your-transfer")}
          step={TOTAL_STEPS}
          totalSteps={TOTAL_STEPS}
        />
        <div className="padding--all margin--bottom bgColor--white rounded--all">
          <div className={`${styles.transferSummaryContainer}`}>
            <span className={`${styles.transferSummarySpan}`}>
              {form.values.instant ? "Instant t" : "T"}ransfer from{" "}
            </span>
            <span className={`${styles.transferSummarySpan}`}>
              {sourceAccount?.getShortDescription()}
            </span>
            <span className={`${styles.transferSummarySpan}`}> to </span>
            <span className={`${styles.transferSummarySpan}`}>
              {destinationAccount?.getShortDescription()}
            </span>
          </div>
          <h2 className="padding--top">
            {moneyFormatter.format(form.values.amount / 100)}
          </h2>
          <hr className="margin--y" />
          <p className={styles.smallTitleFont}>
            {l10n.getString("header-transfer-details-caps")}
          </p>
          <DetailRow
            name={l10n.getString("label-from-account")}
            value={
              shouldShowPullAuthorizationDetails ? (
                fromAccountValue
              ) : (
                <TruncatedAccount
                  name={sourceAccount?.name ?? ""}
                  lastFour={sourceAccount?.getMaskedNumber() ?? ""}
                />
              )
            }
          />
          {shouldShowPullAuthorizationDetails && (
            <DetailRow
              name={l10n.getString(
                "label-from-financial-institution",
                {},
                "From financial institution"
              )}
              value={fromFinancialInstitutionValue}
            />
          )}
          <DetailRow
            name={l10n.getString("label-to-account")}
            value={
              <TruncatedAccount
                name={destinationAccount?.name ?? ""}
                lastFour={destinationAccount?.getMaskedNumber() ?? ""}
              />
            }
          />
          <DetailRow
            name={l10n.getString("label-delivery-speed")}
            value={
              !isExternalTransfer || form.values.instant
                ? l10n.getString("label-instant")
                : l10n.getString("label-ach-speed-short")
            }
          />
          {form.values.instant && (
            <DetailRow
              name={l10n.getString("label-fee")}
              value={
                fee === 0
                  ? l10n.getString("label-no-fee")
                  : moneyFormatter.format(fee)
              }
            />
          )}
          {form.values.memo && (
            <DetailRow
              name={l10n.getString("label-memo")}
              value={form.values.memo}
            />
          )}
          {!form.values.instant && (
            <>
              <DetailRow
                name={l10n.getString("label-frequency")}
                value={form.values.frequency}
              />
              <DetailRow
                name={l10n.getString("label-scheduled-date")}
                value={DateTime.fromISO(form.values.date as string).toFormat(
                  "M/d/yyyy"
                )}
              />
            </>
          )}
        </div>

        {shouldShowPullAuthorizationDetails && (
          <Localized
            id="ach-payment-pull-authorization-disclosure"
            vars={{ institutionName, supportPhoneNumber }}
            elems={{ bold: <strong /> }}
          >
            <div className={styles.disclosureText} />
          </Localized>
        )}

        <ProgressButtons
          backLabel={l10n.getString("button-back")}
          nextLabel={l10n.getString("label-send-money")}
          onBack={() => {
            navigate(`/${TRANSFERS_ROUTE}/${TRANSFER_DETAILS_ROUTE}`);
          }}
          onNext={handleSubmit}
        />
      </ResponsiveFlex>
    </LoadingShim>
  );
}
